import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createScreen } from './utils/state/screensSlice';
import {
    createScreenFromTemplate,
    getTemplatesByCategory,
    ScreenTemplate,
} from './utils/screenTemplates';
import { X, Plus, Filter } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface TemplateLibraryProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateScreen?: (screenData: Partial<ScreenDefinition>) => void;
}

const CATEGORIES = [
    { key: undefined, name: 'All Templates', emoji: '📋' },
    { key: 'attract' as const, name: 'Attract', emoji: '✨' },
    { key: 'capture' as const, name: 'Capture', emoji: '📸' },
    { key: 'review' as const, name: 'Review', emoji: '👀' },
    { key: 'share' as const, name: 'Share', emoji: '📱' },
    { key: 'processing' as const, name: 'Processing', emoji: '⚙️' },
];

export default function TemplateLibrary({
    isOpen,
    onClose,
    onCreateScreen,
}: TemplateLibraryProps) {
    const dispatch = useDispatch();
    const [selectedCategory, setSelectedCategory] = useState<
        ScreenTemplate['category'] | undefined
    >(undefined);

    if (!isOpen) return null;

    const filteredTemplates = getTemplatesByCategory(selectedCategory);

    const handleTemplateSelect = (template: ScreenTemplate) => {
        const newScreen = createScreenFromTemplate(template);

        if (onCreateScreen) {
            onCreateScreen(newScreen);
        } else {
            // Fallback to direct dispatch if no handler provided
            dispatch(createScreen(newScreen));
        }

        onClose();
    };

    return (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 bg-background rounded-lg shadow-xl border border-border w-[600px] max-h-[80vh]">
            <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <Plus className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-foreground">
                            Screen Templates
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Choose a template to get started quickly
                        </p>
                    </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                    <X className="w-5 h-5" />
                </Button>
            </div>

            {/* Category Filter */}
            <div className="p-4 border-b border-border">
                <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                        Category
                    </span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((category) => (
                        <button
                            key={category.key || 'all'}
                            onClick={() => setSelectedCategory(category.key)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                selectedCategory === category.key
                                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                    : 'bg-muted text-foreground hover:bg-muted'
                            }`}
                        >
                            <span className="mr-1">{category.emoji}</span>
                            {category.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Template Grid */}
            <div className="p-4 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                    {filteredTemplates.map((template) => (
                        <div
                            key={template.id}
                            onClick={() => handleTemplateSelect(template)}
                            className="p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/50 hover:border-border transition-all group"
                        >
                            <div className="flex items-start gap-3">
                                <div className="text-2xl">{template.icon}</div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-foreground group-hover:text-blue-600">
                                        {template.name}
                                    </h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {template.description}
                                    </p>
                                    <div className="mt-2 flex items-center gap-2">
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                                            {template.category}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {template.template.elements.length}{' '}
                                            elements
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border bg-muted/50 rounded-b-lg">
                <div className="text-xs text-muted-foreground text-center">
                    Templates provide a starting point. You can customize all
                    properties after adding them.
                </div>
            </div>
        </div>
    );
}
